import React, { useState, useEffect } from 'react';
import { User, Mail, Phone, MapPin, Lock, CheckCircle, XCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';

const Profile = () => {
  const { user, updateProfile, changePassword } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName:  user?.lastName  || '',
    email:     user?.email     || '',
    phone:     user?.phone     || '',
    location:  user?.location  || '',
    bio:       user?.bio       || ''
  });

  const [stats, setStats] = useState({
    completed:    0,
    totalHours:   0,
    certificates: 0,
  });

  const [prefs, setPrefs] = useState({
    emailNotifications: user?.preferences?.emailNotifications !== false,
    pushNotifications:  user?.preferences?.pushNotifications  !== false,
    newsletter:         user?.preferences?.newsletter         === true,
  });

  const [passwordForm, setPasswordForm] = useState({
    current: '',
    next:    '',
    confirm: '',
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  useEffect(() => {
    axios.get('/api/analytics/my-progress')
      .then(res => {
        if (res.data?.success) {
          const s = res.data.stats;
          setStats({
            completed:    s.completed    || 0,
            totalHours:   Math.round((s.totalStudyTime || 0) / 60),
            certificates: s.certificatesEarned || 0,
          });
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    setFormData({
      firstName: user?.firstName || '',
      lastName:  user?.lastName  || '',
      email:     user?.email     || '',
      phone:     user?.phone     || '',
      location:  user?.location  || '',
      bio:       user?.bio       || ''
    });
    setPrefs({
      emailNotifications: user?.preferences?.emailNotifications !== false,
      pushNotifications:  user?.preferences?.pushNotifications  !== false,
      newsletter:         user?.preferences?.newsletter         === true,
    });
  }, [user]);

  if (!user) {
    return (
      <div className="min-h-full bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Accès non autorisé</h1>
          <p className="text-gray-600">Vous devez être connecté pour accéder à votre profil.</p>
        </div>
      </div>
    );
  }

  const formatJoinDate = (dateString) => {
    if (!dateString) return 'Récemment';
    return new Date(dateString).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long' });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    const result = await updateProfile(formData);
    if (result.success) {
      toast.success('Profil mis à jour avec succès');
      setIsEditing(false);
    } else {
      toast.error(result.error || 'Erreur lors de la mise à jour');
    }
  };

  const handleCancel = () => {
    setFormData({
      firstName: user.firstName || '',
      lastName:  user.lastName  || '',
      email:     user.email     || '',
      phone:     user.phone     || '',
      location:  user.location  || '',
      bio:       user.bio       || ''
    });
    setIsEditing(false);
  };

  const handlePrefChange = async (key, value) => {
    const newPrefs = { ...prefs, [key]: value };
    setPrefs(newPrefs);
    await updateProfile({ preferences: newPrefs });
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess(false);

    if (!passwordForm.current || !passwordForm.next) {
      setPasswordError('Veuillez remplir tous les champs');
      return;
    }
    if (passwordForm.next !== passwordForm.confirm) {
      setPasswordError('Les mots de passe ne correspondent pas');
      return;
    }
    if (passwordForm.next.length < 8) {
      setPasswordError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    setPasswordLoading(true);
    const result = await changePassword(passwordForm.current, passwordForm.next);
    setPasswordLoading(false);

    if (result.success) {
      setPasswordSuccess(true);
      setPasswordForm({ current: '', next: '', confirm: '' });
      toast.success('Mot de passe changé avec succès');
    } else {
      setPasswordError(result.error || 'Erreur lors du changement de mot de passe');
    }
  };

  return (
    <div className="min-h-full bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header du profil */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <div className="flex items-center gap-6">
            <div className="w-24 h-24 bg-primary rounded-full flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
              {user.avatar
                ? <img src={user.avatar} alt="avatar" className="w-24 h-24 rounded-full object-cover" />
                : `${user.firstName?.charAt(0) || ''}${user.lastName?.charAt(0) || ''}`}
            </div>
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-text mb-2">
                {user.firstName} {user.lastName}
              </h1>
              <p className="text-gray-600 mb-4">{user.bio || 'Aucune bio disponible'}</p>
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                <span>Membre depuis {formatJoinDate(user.createdAt)}</span>
                <span>{stats.completed} cours terminés</span>
                <span>{stats.totalHours}h d'étude</span>
                <span>{stats.certificates} certificats</span>
              </div>
            </div>
            <button
              onClick={() => setIsEditing(!isEditing)}
              className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors duration-200 flex-shrink-0"
            >
              {isEditing ? 'Annuler' : 'Modifier le profil'}
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Informations personnelles */}
          <div className="md:col-span-2 space-y-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-bold text-text mb-4">Informations personnelles</h2>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <User className="w-5 h-5 text-gray-500" />
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-gray-100"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <User className="w-5 h-5 text-gray-500" />
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-gray-100"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-gray-500" />
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-gray-500" />
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      disabled={!isEditing}
                      placeholder="+33 6 12 34 56 78"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-gray-100"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-gray-500" />
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Localisation</label>
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      disabled={!isEditing}
                      placeholder="Paris, France"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-gray-100"
                    />
                  </div>
                </div>

                <div className="pt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                  <textarea
                    name="bio"
                    value={formData.bio}
                    onChange={handleChange}
                    disabled={!isEditing}
                    rows={4}
                    placeholder="Parlez-nous de vous..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-gray-100"
                  />
                </div>
              </div>

              {isEditing && (
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={handleSave}
                    className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors duration-200"
                  >
                    Sauvegarder
                  </button>
                  <button
                    onClick={handleCancel}
                    className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                  >
                    Annuler
                  </button>
                </div>
              )}
            </div>

            {/* Sécurité */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-bold text-text mb-4">Sécurité</h2>
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mot de passe actuel
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="password"
                      value={passwordForm.current}
                      onChange={e => setPasswordForm(p => ({ ...p, current: e.target.value }))}
                      placeholder="••••••••"
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nouveau mot de passe
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="password"
                      value={passwordForm.next}
                      onChange={e => setPasswordForm(p => ({ ...p, next: e.target.value }))}
                      placeholder="••••••••"
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirmer le nouveau mot de passe
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="password"
                      value={passwordForm.confirm}
                      onChange={e => setPasswordForm(p => ({ ...p, confirm: e.target.value }))}
                      placeholder="••••••••"
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                </div>

                {passwordError && (
                  <div className="flex items-center gap-2 text-red-600 text-sm">
                    <XCircle className="w-4 h-4" />
                    {passwordError}
                  </div>
                )}
                {passwordSuccess && (
                  <div className="flex items-center gap-2 text-green-600 text-sm">
                    <CheckCircle className="w-4 h-4" />
                    Mot de passe changé avec succès
                  </div>
                )}

                <button
                  type="submit"
                  disabled={passwordLoading}
                  className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors duration-200 disabled:opacity-50"
                >
                  {passwordLoading ? 'Chargement...' : 'Changer le mot de passe'}
                </button>
              </form>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Statistiques */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-text mb-4">Statistiques</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Cours terminés</span>
                  <span className="font-semibold">{stats.completed}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Heures d'étude</span>
                  <span className="font-semibold">{stats.totalHours}h</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Certificats</span>
                  <span className="font-semibold">{stats.certificates}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Rôle</span>
                  <span className="font-semibold text-primary capitalize">{user.role || 'Utilisateur'}</span>
                </div>
              </div>
            </div>

            {/* Préférences */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-text mb-4">Préférences</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Notifications email</span>
                  <input
                    type="checkbox"
                    checked={prefs.emailNotifications}
                    onChange={e => handlePrefChange('emailNotifications', e.target.checked)}
                    className="w-4 h-4 text-primary cursor-pointer"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Notifications push</span>
                  <input
                    type="checkbox"
                    checked={prefs.pushNotifications}
                    onChange={e => handlePrefChange('pushNotifications', e.target.checked)}
                    className="w-4 h-4 text-primary cursor-pointer"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Newsletter</span>
                  <input
                    type="checkbox"
                    checked={prefs.newsletter}
                    onChange={e => handlePrefChange('newsletter', e.target.checked)}
                    className="w-4 h-4 text-primary cursor-pointer"
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-text mb-4">Actions</h3>
              <div className="space-y-3">
                <button className="w-full text-left text-gray-600 hover:text-primary transition-colors duration-200">
                  Télécharger mes données
                </button>
                <button className="w-full text-left text-gray-600 hover:text-primary transition-colors duration-200">
                  Exporter mes certificats
                </button>
                <button className="w-full text-left text-red-600 hover:text-red-700 transition-colors duration-200">
                  Supprimer mon compte
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
